import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  Checkbox,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { ResponsiveDialog } from '@/components/common/ResponsiveDialog';
import type { BuildVersion, VersionInfo } from '@/api/releaseApproval';
import {
  displayName,
  type InheritedChange,
  type NotTestedChange,
  type OpenFinding,
  type UntestedChange,
} from './testResults';

const STATUS_LABEL = { ok: 'checklist.ok', not_ok: 'checklist.notOk', not_tested: 'checklist.notTested' } as const;

interface ApproveReleaseDialogProps {
  open: boolean;
  candidate: BuildVersion;
  production: VersionInfo | null;
  changeCount: number;
  /** Not-ok marks still standing (#3491): shown, and acknowledged before approving. */
  openFindings?: readonly OpenFinding[];
  /** Changes nobody marked (#3526): shown, and acknowledged separately before approving. */
  untestedChanges?: readonly UntestedChange[];
  /** Changes testers could not test here (#3750): shown with reasons, not acknowledged. */
  notTestedChanges?: readonly NotTestedChange[];
  /** Changes marked only on earlier versions (#3547): shown for information. */
  inheritedChanges?: readonly InheritedChange[];
  submitting: boolean;
  onClose: () => void;
  onConfirm: (comment: string) => void;
}

/** Last step before the version goes to the customer's production (#3316). */
export function ApproveReleaseDialog({
  open,
  candidate,
  production,
  changeCount,
  openFindings = [],
  untestedChanges = [],
  notTestedChanges = [],
  inheritedChanges = [],
  submitting,
  onClose,
  onConfirm,
}: ApproveReleaseDialogProps) {
  const { t } = useTranslation('releaseApproval');
  const { t: tc } = useTranslation('common');
  const [comment, setComment] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);
  const [untestedAcknowledged, setUntestedAcknowledged] = useState(false);
  // Each kind is acknowledged on its own: reading the findings is not
  // accepting changes nobody tested (#3526). staff-api checks the same.
  const needsAcknowledgement =
    (openFindings.length > 0 && !acknowledged) || (untestedChanges.length > 0 && !untestedAcknowledged);

  return (
    <ResponsiveDialog
      open={open}
      onClose={submitting ? undefined : onClose}
      mobileTitle={t('dialog.title')}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ display: { xs: 'none', sm: 'block' } }}>{t('dialog.title')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Alert severity="warning">{t('dialog.warning')}</Alert>
          <Typography>
            {t('dialog.versions', {
              candidate: candidate.version,
              production: production?.version ?? t('unknownVersion'),
            })}
          </Typography>
          <Typography color="text.secondary">{t('dialog.changeCount', { count: changeCount })}</Typography>
          {openFindings.length > 0 && (
            <Alert severity="error" data-testid="open-findings">
              <Typography fontWeight={600}>{t('dialog.openFindings', { count: openFindings.length })}</Typography>
              <List dense disablePadding>
                {openFindings.map((finding) => (
                  <ListItem key={`${finding.noteId}-${finding.by}`} disableGutters>
                    <ListItemText
                      primary={finding.title}
                      secondary={`${finding.comment} (${displayName(finding.by)})`}
                    />
                  </ListItem>
                ))}
              </List>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={acknowledged}
                    onChange={(event) => {
                      setAcknowledged(event.target.checked);
                    }}
                    inputProps={{ 'aria-label': t('dialog.acknowledgeFindings') }}
                  />
                }
                label={t('dialog.acknowledgeFindings')}
              />
            </Alert>
          )}
          {untestedChanges.length > 0 && (
            <Alert severity="warning" data-testid="untested-changes">
              <Typography fontWeight={600}>{t('dialog.untestedChanges', { count: untestedChanges.length })}</Typography>
              <List dense disablePadding>
                {untestedChanges.map((change) => (
                  <ListItem key={change.noteId} disableGutters>
                    <ListItemText primary={change.title} />
                  </ListItem>
                ))}
              </List>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={untestedAcknowledged}
                    onChange={(event) => {
                      setUntestedAcknowledged(event.target.checked);
                    }}
                    inputProps={{ 'aria-label': t('dialog.acknowledgeUntested') }}
                  />
                }
                label={t('dialog.acknowledgeUntested')}
              />
            </Alert>
          )}
          {notTestedChanges.length > 0 && (
            <Alert severity="info" data-testid="not-tested-changes">
              <Typography fontWeight={600}>{t('dialog.notTestedChanges', { count: notTestedChanges.length })}</Typography>
              <Typography variant="body2">{t('dialog.notTestedChangesHelp')}</Typography>
              <List dense disablePadding>
                {notTestedChanges.map((change) => (
                  <ListItem key={change.noteId} disableGutters>
                    <ListItemText
                      primary={change.title}
                      secondary={change.marks
                        .map((m) =>
                          m.reason
                            ? t('dialog.notTestedMark', { name: displayName(m.by), reason: m.reason })
                            : displayName(m.by)
                        )
                        .join(', ')}
                    />
                  </ListItem>
                ))}
              </List>
            </Alert>
          )}
          {inheritedChanges.length > 0 && (
            <Alert severity="info" data-testid="inherited-changes">
              <Typography fontWeight={600}>{t('dialog.inheritedChanges', { count: inheritedChanges.length })}</Typography>
              <Typography variant="body2">{t('dialog.inheritedChangesHelp')}</Typography>
              <List dense disablePadding>
                {inheritedChanges.map((change) => (
                  <ListItem key={change.noteId} disableGutters>
                    <ListItemText
                      primary={change.title}
                      secondary={change.marks
                        .map((m) =>
                          t('dialog.inheritedMark', {
                            name: displayName(m.by),
                            status: t(STATUS_LABEL[m.status]),
                            version: m.version ?? '',
                          })
                        )
                        .join(', ')}
                    />
                  </ListItem>
                ))}
              </List>
            </Alert>
          )}
          <TextField
            label={t('dialog.comment')}
            helperText={t('dialog.commentHelp')}
            value={comment}
            onChange={(event) => {
              setComment(event.target.value);
            }}
            multiline
            minRows={2}
            inputProps={{ maxLength: 1000 }}
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          {tc('cancel')}
        </Button>
        <Button
          variant="contained"
          color="warning"
          disabled={submitting || needsAcknowledgement}
          onClick={() => {
            onConfirm(comment.trim());
          }}
        >
          {t('dialog.confirm')}
        </Button>
      </DialogActions>
    </ResponsiveDialog>
  );
}
